import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {RoughUnderline} from "@khanacademy/khannotations";
import {View} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {staticUrl} from "@khan/urls";

import {MarketingColors} from "#marketing-content-shared-package/constants.ts";
import * as mq from "#marketing-content-shared-package/media-queries.ts";
import {hideScrollButtonOnMobileMq} from "#marketing-content-shared-package/scroll-button.tsx";
import {
    SansSerifBody,
    SerifHeadingHero,
} from "#marketing-content-shared-package/typography/typography.ts";

type Props = {
    kind: "Hero";
};

export default class Hero extends React.Component<Props> {
    componentDidMount() {
        // Normally, we'd use the native `vh` element, but this element suffers
        // from a problem: it does not exclude browser UI like address bars.
        // This means we have no CSS-only way to determine the viewport size.
        // We'll use JS and set it as a CSS variable.
        // Note that this variable is NOT dynamic - it does not resize - and
        // so it should not be used outside this component.
        //
        // TODO (josh): Dedupe with logged-out-homepage hero, into a shared
        // helper component?
        if (document.documentElement && document.documentElement.style) {
            document.documentElement.style.setProperty(
                "--hero-screen-height",
                `${window.innerHeight}px`,
            );
            window.addEventListener("orientationchange", this.setHeightVar);
        }
    }

    componentWillUnmount() {
        window.removeEventListener("orientationchange", this.setHeightVar);
    }

    setHeightVar: () => void = () => {
        // Because the orientation change event fires before the window width
        // and height update, we need to update the variable to be the width
        // of the window as that will be the new height once the orientation
        // change has completed.
        if (document.documentElement && document.documentElement.style) {
            document.documentElement.style.setProperty(
                "--hero-screen-height",
                `${window.innerWidth}px`,
            );
        }
    };

    renderMainContent(): React.ReactNode {
        return (
            <View style={styles.mainContent}>
                <SerifHeadingHero tag="h1" style={styles.title}>
                    <Trans>
                        Millions of students lack opportunity.{" "}
                        <RoughUnderline
                            animation={speedAnimationHero1}
                            roughStyle={underlineStyleHero1}
                        >
                            Together
                        </RoughUnderline>
                        , we can give it to them.
                    </Trans>
                </SerifHeadingHero>
                <SansSerifBody tag="p" style={styles.copy}>
                    <span className={css(styles.whiteHighlightInline)}>
                        {t`We are a nonprofit with the mission to provide a free, world-class education to anyone, anywhere`}
                    </span>
                </SansSerifBody>
            </View>
        );
    }

    render(): React.ReactNode {
        return (
            <View style={styles.container}>
                <View style={styles.content}>
                    <View style={styles.imgContainer}>
                        <img
                            alt=""
                            aria-hidden={true}
                            src={staticUrl(
                                "/images/lohp/donor-landing-hero@2x.png",
                            )}
                            className={css(styles.illustration)}
                        />
                        <img
                            alt=""
                            aria-hidden={true}
                            src={staticUrl(
                                "/images/lohp/donor-landing-hero-dots@2x.png",
                            )}
                            className={css(styles.supportIllustration)}
                        />
                    </View>
                    <View style={styles.copyContainer}>
                        {this.renderMainContent()}
                    </View>
                </View>
            </View>
        );
    }
}

const speedAnimationHero1 = {
    animation: "speed",
    speed: 10,
    delay: 1200,
} as const;

const underlineStyleHero1 = {
    roughness: 0,
    stroke: MarketingColors.highlightTeal,
    strokeWidth: 2,
    bowing: {
        bowing: "inverse",
        ratio: 100,
    },
    lift: 4,
} as const;

const styles = StyleSheet.create({
    container: {
        // When 100vh != --hero-screen-height, there might be some
        // reshuffling. Make that smooth.
        transition: "min-height 200ms ease-in-out",

        [mq.largePhoneOrSmaller]: {
            minHeight: "initial",
            height: "calc(var(--hero-screen-height, 100vh) - 60px)",
            [hideScrollButtonOnMobileMq]: {
                // This means we are on a phone-width device that is really,
                // really long. Give up on trying to fill the hero on the
                // entire page.
                height: "auto",
            },
        },
        [mq.smallTabletOnly]: {
            minHeight: "calc(var(--hero-screen-height, 100vh) - 60px)",
        },
        [mq.tabletOnly]: {
            minHeight: "calc(var(--hero-screen-height, 100vh) - 120px)",
        },
        [mq.smallDesktopOrLarger]: {
            height: "calc(100vh - 120px)",
            minHeight: 550,
            maxHeight: 700,
        },
    },
    content: {
        flexDirection: "column",
        paddingLeft: spacing.large_24,
        paddingRight: spacing.large_24,

        [mq.smallTabletOnly]: {
            paddingLeft: spacing.xLarge_32,
            paddingRight: spacing.xLarge_32,
        },
        [mq.tabletOnly]: {
            paddingLeft: 110,
            paddingRight: 110,
        },
        [mq.smallDesktopOrLarger]: {
            flexDirection: "row",
            paddingTop: 56,
            paddingLeft: spacing.xxxLarge_64,
            paddingRight: spacing.xxxLarge_64,
            maxWidth: 1308,
            margin: "auto",
        },
    },
    imgContainer: {
        flex: 1,
        position: "absolute",
        left: 0,
        right: 0,
        height: 0,
        paddingTop: 546,

        // Going from overlapping vertical layout to side-by-side layout
        [mq.smallDesktopOrLarger]: {
            position: "static",
            paddingTop: 0,
            height: "auto",
        },
    },
    illustration: {
        width: 620,
        margin: `-${spacing.xLarge_32}px auto 0`,
        position: "absolute",
        top: 0,
        left: "50%",
        transform: "translateX(-45%)",

        [mq.tabletOrLarger]: {
            marginTop: -15,
            transform: "translateX(-50%)",
        },
        // Going from overlapping vertical layout to side-by-side layout
        [mq.smallDesktopOrLarger]: {
            margin: 0,
            position: "relative",
            top: "auto",
            left: "auto",
            transform: "none",
            zIndex: 1,
            width: "116%",
            marginLeft: "-54px",
        },
        [mq.xlDesktopOrLarger]: {
            marginLeft: -spacing.xxxLarge_64,
            marginTop: "-8%",
        },
    },
    supportIllustration: {
        position: "absolute",
        width: 226,
        bottom: -80,
        right: -100,

        [mq.smallTabletOrLarger]: {
            right: "auto",
            left: 480,
        },
        [mq.smallDesktopOrLarger]: {
            left: 0,
            bottom: -20,
            width: "19%",
            zIndex: 0,
        },
    },
    copyContainer: {
        flex: 1,
        flexDirection: "row",
        marginTop: 376,

        [mq.smallDesktopOrLarger]: {
            marginTop: 0,
        },
    },
    mainContent: {
        // By default, we don't want items to stretch horizontally.
        alignItems: "flex-start",
        paddingBottom: spacing.xxxLarge_64,

        [mq.smallDesktopOrLarger]: {
            alignSelf: "center",
            paddingBottom: 0,
        },

        [mq.shortPhoneOnly]: {
            alignItems: "stretch",
        },

        [mq.tallPhoneOnly]: {
            maxWidth: 300,
        },

        [mq.largePhoneOrSmaller]: {
            paddingBottom: spacing.xLarge_32,
        },
    },
    title: {
        [mq.tabletOnly]: {
            maxWidth: "80%",
        },
        [mq.smallTabletOnly]: {
            maxWidth: "80%",
        },
        [mq.largePhoneOrSmaller]: {
            marginBottom: spacing.medium_16,
        },
        [mq.tallPhoneOnly]: {
            marginBottom: 40,
        },
    },
    copy: {
        [mq.largePhoneOrSmaller]: {
            display: "none",
        },
        [mq.xlDesktopOrLarger]: {
            marginRight: 46,
            marginBottom: 40,
        },
    },
    whiteHighlightInline: {
        // Avoid clipping in ?lang=ka
        verticalAlign: "text-bottom",
        background: "white",
        display: "inline",
    },
});
